<template>
  <section class="space-y-4">
    <div class="flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 class="text-base font-semibold text-slate-900">{{ contentTitle }}</h3>
        <p class="mt-1 text-sm text-slate-500">{{ contentDescription }}</p>
      </div>
      <span v-if="displayPhase !== 'none'" class="status-badge bg-slate-100 text-slate-700">
        当前展示：{{ displayPhase === 'summary' ? '总结' : '计划' }}
      </span>
    </div>

    <div v-if="versionOptions.length" class="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-xs font-medium text-slate-500">版本</span>
        <button
          v-for="version in versionOptions"
          :key="version.key"
          type="button"
          class="inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
          :class="version.selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50/60 hover:text-blue-700'"
          :disabled="loadingVersion"
          @click="emit('select-version', version.raw)"
        >
          <span>{{ version.label }}</span>
          <span v-if="version.current" class="rounded bg-slate-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-slate-500">当前</span>
          <span v-if="version.attachments?.length" class="text-[0.65rem] text-slate-400">附件 {{ version.attachments.length }}</span>
        </button>
        <span v-if="loadingVersion" class="text-xs text-slate-400">加载中...</span>
      </div>
      <div v-if="activeVersion" class="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5">
        <div class="flex flex-wrap items-center justify-between gap-1.5">
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="status-badge bg-slate-100 text-slate-700">{{ activeVersionLabel }}</span>
            <span v-if="activeVersion.lateAdjustment" class="status-badge bg-amber-50 text-amber-700">迟交后调整</span>
          </div>
          <time class="text-xs text-slate-400">{{ formatReportDisplayDateTime(activeVersion.occurredAt) }}</time>
        </div>
        <p v-if="activeVersion.action || activeVersion.reason" class="mt-1 break-words text-xs leading-5 text-slate-500">
          {{ versionActionLabel(activeVersion.action) }}<span v-if="activeVersion.reason">：{{ activeVersion.reason }}</span>
        </p>
      </div>
    </div>

    <div v-if="displayPhase === 'none'" class="data-table-empty is-compact">该员工尚未提交今日计划或总结。</div>

    <template v-else>
      <div v-if="items.length === 0" class="data-table-empty is-compact">{{ emptyWorkItemText }}</div>

      <div v-else class="grid gap-2 md:grid-cols-2">
        <ReportWorkItemEditor
          v-for="(item, index) in items"
          :key="item.id || item.clientKey || index"
          :item="item"
          :phase="displayPhase"
          :readonly="true"
          :annotation-count="workItemAnnotationCount(item)"
          @open-annotations="emit('open-annotations', item)"
        />
      </div>

      <section class="border-t border-slate-200 pt-4">
        <div class="mb-2">
          <h3 class="text-sm font-semibold text-slate-800">{{ overviewTitle }}</h3>
          <p class="mt-1 text-xs leading-5 text-slate-500">{{ overviewDescription }}</p>
        </div>
        <dl class="grid gap-2 md:grid-cols-2">
          <div
            v-for="field in activeFields"
            :key="field.key"
            class="min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm"
            :class="field.class"
          >
            <dt class="text-xs font-medium text-slate-500">{{ field.label }}</dt>
            <dd class="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{{ valueOf(field.value) }}</dd>
          </div>
        </dl>
      </section>
    </template>

    <p class="sr-only">待办快照 {{ snapshotCount }} {{ items.some((item) => item?.currentTodoSnapshot) ? 'currentTodoSnapshot' : '' }}</p>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import ReportWorkItemEditor from './ReportWorkItemEditor.vue'
import { formatReportDisplayDateTime, workReportPhaseLabel } from '../workReportUtils'

const SUBMITTED_PHASE_STATUSES = new Set(['SUBMITTED', 'PENDING_SUPPLEMENT', 'LATE'])
const VERSION_ACTION_LABELS = {
  SUBMIT: '提交',
  ADJUST: '调整',
  SUPPLEMENT: '补充'
}

const props = defineProps({
  report: {
    type: Object,
    default: null
  },
  workItems: {
    type: Array,
    default: () => []
  },
  versions: {
    type: Array,
    default: () => []
  },
  selectedVersion: {
    type: Object,
    default: null
  },
  loadingVersion: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select-version', 'open-annotations'])

const selectedVersionPayload = computed(() => props.selectedVersion || null)
const displayReport = computed(() => props.selectedVersion || props.report)
const items = computed(() => {
  if (Array.isArray(displayReport.value?.workItems)) return displayReport.value.workItems
  return Array.isArray(props.workItems) ? props.workItems : []
})
const planStatus = computed(() => displayReport.value?.statusSummary?.planStatus || displayReport.value?.planStatus)
const summaryStatus = computed(() => displayReport.value?.statusSummary?.summaryStatus || displayReport.value?.summaryStatus)
const hasSubmittedSummary = computed(() => isSubmittedPhase(summaryStatus.value, displayReport.value?.summarySubmittedAt))
const hasSubmittedPlan = computed(() => isSubmittedPhase(planStatus.value, displayReport.value?.planSubmittedAt))
const displayPhase = computed(() => {
  const selectedPhase = versionPhase(selectedVersionPayload.value?.phase)
  if (selectedPhase) return selectedPhase
  if (hasSubmittedSummary.value) return 'summary'
  if (hasSubmittedPlan.value) return 'plan'
  if (!displayReport.value && items.value.length) return 'summary'
  return 'none'
})
const versionOptions = computed(() => {
  const versions = Array.isArray(props.versions) ? props.versions : []
  const selectedKey = selectedVersionPayload.value ? versionKey(selectedVersionPayload.value) : ''
  return versions.map((version) => {
    const key = versionKey(version)
    const current = currentPhaseVersion(version.phase) === normalizeVersionNo(version.versionNo)
    return {
      raw: version,
      key,
      label: `${workReportPhaseLabel(version.phase)} v${valueOf(version.versionNo)}`,
      current,
      selected: selectedKey ? key === selectedKey : current && versionPhase(version.phase) === displayPhase.value,
      attachments: Array.isArray(version.attachments) ? version.attachments : []
    }
  })
})
const activeVersion = computed(() => {
  if (selectedVersionPayload.value) return selectedVersionPayload.value
  return versionOptions.value.find((version) => version.selected)?.raw || null
})
const activeVersionLabel = computed(() => {
  if (!activeVersion.value) return ''
  return `${workReportPhaseLabel(activeVersion.value.phase)} v${valueOf(activeVersion.value.versionNo)}`
})
const contentTitle = computed(() => {
  if (displayPhase.value === 'summary') return '总结内容'
  if (displayPhase.value === 'plan') return '计划内容'
  return '暂无日报内容'
})
const contentDescription = computed(() => {
  if (selectedVersionPayload.value && displayPhase.value === 'summary') return '当前展示所选总结版本快照。'
  if (selectedVersionPayload.value && displayPhase.value === 'plan') return '当前展示所选计划版本快照。'
  if (displayPhase.value === 'summary') return '总结已提交，当前展示计划目标与执行结果。'
  if (displayPhase.value === 'plan') return '总结未提交，当前展示已提交计划。'
  return '计划和总结均未提交，暂无可查看内容。'
})
const emptyWorkItemText = computed(() => displayPhase.value === 'summary' ? '暂无工作项，可查看总结概要。' : '暂无工作项')
const planFields = computed(() => [
  { key: 'planFocus', label: '今日重点', value: displayReport.value?.planFocus },
  { key: 'planCollaborationNeed', label: '需要协同', value: displayReport.value?.planCollaborationNeed },
  { key: 'planSupplement', label: '补充说明', value: displayReport.value?.planSupplement, class: 'md:col-span-2' }
])
const summaryFields = computed(() => [
  { key: 'summaryAchievement', label: '今日成果', value: displayReport.value?.summaryAchievement, class: 'md:col-span-2' },
  { key: 'summaryBlocker', label: '阻塞事项', value: displayReport.value?.summaryBlocker },
  { key: 'summaryAssistanceNeeded', label: '需要协助', value: displayReport.value?.summaryAssistanceNeeded },
  { key: 'summarySupplement', label: '补充说明', value: displayReport.value?.summarySupplement, class: 'md:col-span-2' }
])
const activeFields = computed(() => displayPhase.value === 'summary' ? summaryFields.value : planFields.value)
const overviewTitle = computed(() => displayPhase.value === 'summary' ? '总结概要' : '计划概要')
const overviewDescription = computed(() => displayPhase.value === 'summary'
  ? '展示该日报的总结正文，不在团队工作台内编辑。'
  : '展示该日报的计划正文，不在团队工作台内编辑。')
const snapshotCount = computed(() => items.value.filter((item) => item?.todoSnapshot || item?.currentTodoSnapshot).length)

function isSubmittedPhase(status, submittedAt) {
  if (submittedAt) return true
  return SUBMITTED_PHASE_STATUSES.has(status)
}

function versionPhase(phase) {
  const normalized = String(phase || '').toUpperCase()
  if (normalized === 'SUMMARY') return 'summary'
  if (normalized === 'PLAN') return 'plan'
  return ''
}

function versionKey(version) {
  return `${String(version?.phase || '').toUpperCase()}-${normalizeVersionNo(version?.versionNo)}`
}

function currentPhaseVersion(phase) {
  const normalized = versionPhase(phase)
  const value = normalized === 'summary' ? props.report?.summaryVersion : props.report?.planVersion
  return normalizeVersionNo(value)
}

function normalizeVersionNo(value) {
  if (value === undefined || value === null || value === '') return ''
  return String(value)
}

function versionActionLabel(action) {
  const normalized = String(action || '').toUpperCase()
  return VERSION_ACTION_LABELS[normalized] || '版本变更'
}

function valueOf(value) {
  if (value === undefined || value === null || value === '') return '--'
  if (Array.isArray(value)) return value.length ? value.join('、') : '--'
  if (typeof value === 'object') return JSON.stringify(value)
  return value
}

function workItemAnnotationCount(item) {
  const count = Number(item?.todoAnnotationCount ?? item?.currentTodoSnapshot?.annotationCount ?? item?.todoSnapshot?.annotationCount)
  return Number.isFinite(count) ? count : 0
}
</script>
